import { useMutation } from 'react-relay';
import CREATE_POST_MUTATION, { type CreatePostMutation } from '../../graphql/__generated__/CreatePostMutation.graphql';

export type PostFormData = {
  title: string;
  content: string | object; // Can be string or JSON document object from Editor
  category: string;
  tags?: string;
};

interface ValidationErrors {
  title?: string;
  content?: string;
  category?: string;
  tags?: string;
}

interface CreatePostOptions {
  onSuccess?: (post: any) => void;
  onError?: (errors: string[]) => void;
}

const useCreatePost = () => {
  const [commit, isPending] = useMutation<CreatePostMutation>(CREATE_POST_MUTATION);

  const createPost = async (formData: PostFormData, options?: CreatePostOptions) => {
    validateForm(formData);

    // Convert comma-separated tags string to array
    const tagsArray = formData.tags
      ? formData.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0)
      : null;

    // Always serialize content to string
    const serializedContent = JSON.stringify(formData.content);

    commit({
      variables: {
        title: formData.title,
        content: serializedContent,
        type: 'QUESTION',
        categoryId: formData.category,
        tags: tagsArray,
      },
      onCompleted: (response, errors) => {
        if (errors) {
          const errorMessages = errors.map((error) => error.message);
          options?.onError?.(errorMessages);
          return;
        }

        const result = response?.unified?.ai?.createPost;

        if (result?.success && result.post) {
          options?.onSuccess?.(result.post);
        } else if (result?.errors && result.errors.length > 0) {
          const errorMessages = result.errors.filter((error) => error?.message).map((error) => error!.message);
          options?.onError?.(errorMessages);
        } else {
          options?.onError?.(['An unexpected error occurred']);
        }
      },
      onError: (error) => {
        options?.onError?.([error.message]);
      },
    });
  };

  return { createPost, isPending };
};

export default useCreatePost;

const validateForm = (data: PostFormData) => {
  const errors: ValidationErrors = {};

  if (!data.title.trim()) {
    errors.title = 'Title is required';
  }

  // Handle content validation for both string and object types
  if (typeof data.content === 'string') {
    if (!data.content.trim()) {
      errors.content = 'Content is required';
    }
  } else if (typeof data.content === 'object') {
    // For object content (from Editor), check if it has meaningful content
    const contentStr = JSON.stringify(data.content);
    if (!contentStr || contentStr === '{"version":1,"type":"doc","content":[]}') {
      errors.content = 'Content is required';
    }
  } else {
    errors.content = 'Content is required';
  }

  if (!data.category) {
    errors.category = 'Please select a product';
  }

  if (Object.keys(errors).length > 0) {
    throw errors;
  }
};
